// src/components/ProductModal.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import Select from 'react-select'; // <-- Import react-select
import { toast } from 'react-toastify';
import { useData } from '../context/DataContext'; // Import useData
import styles from './ProductModal.module.css'; // We'll create this CSS file next
import Spinner from "./Spinner";

// Ensure react-modal is configured (usually in App.js or index.js)
// import Modal from 'react-modal';
// Modal.setAppElement('#root'); // Or your app's root element ID

const ADD_NEW_PRODUCT_VALUE = 'new'; // Constant for the "Add New" option

// --- Custom Styles for react-select to match dark theme ---
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: '#3a3a3a',
        borderColor: state.isFocused ? '#6f9aee' : '#555',
        boxShadow: state.isFocused ? '0 0 0 1px #6f9aee' : 'none', // Adjusted focus shadow
        '&:hover': {
            borderColor: state.isFocused ? '#6f9aee' : '#777'
        },
        cursor: 'pointer',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#2d2d2d', // Dark background for the menu
        zIndex: 10, // Ensure menu is above other modal content if needed
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#4a4a4a' : state.isFocused ? '#3a3a3a' : '#2d2d2d', // Dark theme colors
        color: '#e0e0e0',
        cursor: 'pointer',
        '&:active': { // Keep active style subtle
            backgroundColor: '#4a4a4a',
        },
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#e0e0e0', // Light text for selected value
    }),
    input: (provided) => ({
        ...provided,
        color: '#e0e0e0', // Light text for input/search
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#aaa', // Lighter placeholder text
    }),
    indicatorSeparator: () => ({
        display: 'none', // Hide the separator
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: '#aaa', // Style the dropdown arrow
        '&:hover': {
            color: '#ccc',
        },
    }),
};
// --- End Custom Styles ---

const ProductModal = () => {
    const {
        isProductModalOpen,
        setIsProductModalOpen,
        products, // { productsListId, productName, description, goToMarket } <= Assumed structure
        selectedProductId,
        setSelectedProductId,
        createProduct, // Assumed signature: createProduct(name, description, goToMarket)
        updateProduct, // Assumed signature: updateProduct(id, name, description, goToMarket)
        deleteProduct,
        isProductModalLoading,
    } = useData();

    // Local state for the input fields
    const [currentProductName, setCurrentProductName] = useState('');
    const [currentProductDescription, setCurrentProductDescription] = useState('');
    const [currentGoToMarket, setCurrentGoToMarket] = useState(''); // <-- New state for Go-to-market
  

    // Effect to update local form state when selectedProductId changes or products list updates
    useEffect(() => {
        if (!isProductModalOpen) return; // Don't update if modal is closed

        if (selectedProductId === ADD_NEW_PRODUCT_VALUE) {
            setCurrentProductName('');
            setCurrentProductDescription('');
            setCurrentGoToMarket(''); // <-- Reset new field
        } else {
            const selectedProduct = products.find(p => p.productsListId === selectedProductId);
            if (selectedProduct) {
                setCurrentProductName(selectedProduct.productName);
                setCurrentProductDescription(selectedProduct.description || '');
                setCurrentGoToMarket(selectedProduct.goToMarket || ''); // <-- Set new field value (handle null/undefined)
            } else {
                // Product might have been deleted, reset form
                setCurrentProductName('');
                setCurrentProductDescription('');
                setCurrentGoToMarket(''); // <-- Reset new field
                // Optionally set an error or warning here if needed
            }
        }
        // Only need to depend on the *data* influencing the state, not the setters themselves
    }, [selectedProductId, products, isProductModalOpen]);

    const handleCloseRequest = useCallback(() => {
        if (isProductModalLoading) return; // Don't close while loading
        setIsProductModalOpen(false);
    }, [isProductModalLoading, setIsProductModalOpen]);


    // --- Handle change for react-select ---
    const handleDropdownChange = (selected) => {
        if (selected) {
            setSelectedProductId(selected.value);
        } else {
            setSelectedProductId(ADD_NEW_PRODUCT_VALUE);
        }
    };

    // --- Calculate max menu height dynamically (optional but good) ---
    const modalBodyRef = useRef(null);
    const [maxMenuHeight, setMaxMenuHeight] = useState(250);


    const handleSave = async () => {
        if (!currentProductName.trim()) {
            toast.error("Product name cannot be empty.");
            return;
        }

        let result;
        const productData = {
            name: currentProductName,
            description: currentProductDescription,
            goToMarket: currentGoToMarket // <-- Include new field data
        };

        if (selectedProductId === ADD_NEW_PRODUCT_VALUE) {
            // Create new product
            console.log(`[ProductModal] Attempting to CREATE: Name='${productData.name}', Desc='${productData.description}', GTM='${productData.goToMarket}'`);
            // Pass all relevant fields to createProduct
            result = await createProduct(productData.name, productData.description, productData.goToMarket);
            if (result?.status && result?.data) {
                toast.success('Product created successfully!');
                setSelectedProductId(result.data.productsListId);
            }
        } else {
            // Update existing product
            console.log(`[ProductModal] Attempting to UPDATE ID ${selectedProductId}: Name='${productData.name}', Desc='${productData.description}', GTM='${productData.goToMarket}'`);
            // Pass all relevant fields to updateProduct
            result = await updateProduct(selectedProductId, productData.name, productData.description, productData.goToMarket);
            if (result?.status) {
                toast.success("Product settings successfully saved!");
            } else {
                toast.error(result?.message || 'Failed to update product.');
            }
        }
        // Error handling is managed by the context
    };

    const handleDelete = async () => {
        if (selectedProductId === ADD_NEW_PRODUCT_VALUE || isProductModalLoading) {
            return;
        }

        let result;
        const selectedProduct = products.find(p => p.productsListId === selectedProductId);
        const confirmDelete = window.confirm(`Are you sure you want to delete the product "${selectedProduct?.productName || 'this product'}"? This action cannot be undone.`);

        if (confirmDelete) {
            console.log(`[ProductModal] Attempting to DELETE ID ${selectedProductId}`);
            result = await deleteProduct(selectedProductId);
            if (result?.status) {
                toast.success("Product successfully deleted!");
            // Selection resets to 'new' via context if successful
            } else {
                toast.error(result?.message || 'Failed to delete product.');
            }
        }
    };

    // Memoize product options
    const productOptions = useMemo(() => {
        const options = [
            { value: ADD_NEW_PRODUCT_VALUE, label: '-- Add a new product --' }
        ];
        products.forEach(product => {
            options.push({
                value: product.productsListId,
                label: product.productName
                // Add description/GTM to label if desired for dropdown display?
                // label: `${product.productName} (${product.description?.substring(0, 20) || ''}...)`
            });
        });
        return options;
    }, [products]);

    // Get the currently selected option object for react-select
    const selectedOption = useMemo(() => {
        return productOptions.find(option => option.value === selectedProductId) || null;
    }, [selectedProductId, productOptions]);

    // Effect for max menu height (remains unchanged)
    useEffect(() => {
        if (isProductModalOpen && modalBodyRef.current) {
            const modalRect = modalBodyRef.current.getBoundingClientRect();
            const calculatedHeight = modalRect.height * 0.6 - 50;
            setMaxMenuHeight(Math.max(200, calculatedHeight));
        }
    }, [isProductModalOpen]);

    return (
        <Modal
            isOpen={isProductModalOpen}
            onRequestClose={handleCloseRequest}
            className={styles.modalContent}
            overlayClassName={styles.modalOverlay}
            contentLabel="Manage Products"
            shouldCloseOnOverlayClick={!isProductModalLoading}
            shouldCloseOnEsc={!isProductModalLoading}
            aria={{ modal: 'true' }}
        >
            <h2 id="productModalTitle">Manage Products</h2>

            {/* Add ref to modal body */}
            <div className={styles.modalBody} ref={modalBodyRef}>
                {/* Left Side: Dropdown */}
                <div className={styles.leftPanel}>
                    <label htmlFor="productSelect" className={styles.panelLabel}>Select Product:</label>
                    <Select
                        inputId="productSelect"
                        value={selectedOption}
                        onChange={handleDropdownChange}
                        options={productOptions}
                        styles={customSelectStyles}
                        maxMenuHeight={maxMenuHeight}
                        isDisabled={isProductModalLoading}
                        classNamePrefix="react-select"
                        placeholder="Select or add product..."
                        isSearchable={true}
                    />
                </div>
                {isProductModalLoading && <Spinner/>}
                {/* Right Side: Inputs - Now scrollable */}
                <div className={styles.rightPanel}>
                    <div className={styles.formGroup}>
                        <label htmlFor="productNameInput">Product Name:</label>
                        <input
                            type="text"
                            id="productNameInput"
                            value={currentProductName}
                            onChange={(e) => setCurrentProductName(e.target.value)}
                            placeholder="Enter product name"
                            disabled={isProductModalLoading}
                            required
                            aria-required="true"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="productDescriptionInput">Product Description:</label>
                        <textarea
                            id="productDescriptionInput"
                            value={currentProductDescription}
                            onChange={(e) => setCurrentProductDescription(e.target.value)}
                            rows="5" // Keep description relatively short
                            placeholder="Enter product description (optional)"
                            disabled={isProductModalLoading}
                        />
                    </div>
                    {/* --- New Go-to-market Field --- */}
                    <div className={styles.formGroup}>
                        <label htmlFor="goToMarketInput">Go-to-market Strategy:</label>
                        <textarea
                            id="goToMarketInput"
                            value={currentGoToMarket}
                            onChange={(e) => setCurrentGoToMarket(e.target.value)}
                            rows="7" // Give more space for strategy
                            placeholder="Outline the go-to-market strategy (optional)"
                            disabled={isProductModalLoading}
                        />
                    </div>
                     {/* --- End New Field --- */}
                </div>
            </div>

            {/* Bottom: Action Buttons */}
            <div className={styles.modalActions}>
                 <button
                     onClick={handleSave}
                     className={`${styles.actionButton} ${styles.saveButton}`}
                     disabled={isProductModalLoading || !currentProductName.trim()}
                 >
                     {isProductModalLoading ? 'Saving...' : (selectedProductId === ADD_NEW_PRODUCT_VALUE ? 'Create' : 'Save Changes')}
                 </button>

                 <button
                     onClick={handleDelete}
                     className={`${styles.actionButton} ${styles.deleteButton}`}
                     disabled={isProductModalLoading || selectedProductId === ADD_NEW_PRODUCT_VALUE}
                 >
                     {isProductModalLoading ? 'Deleting...' : 'Delete Product'}
                 </button>

                 <button
                     onClick={handleCloseRequest}
                     className={`${styles.actionButton} ${styles.closeButton}`}
                     disabled={isProductModalLoading}
                 >
                     Close
                 </button>
            </div>
        </Modal>
    );
};

ProductModal.propTypes = {};

export default ProductModal;